import { FC, useCallback } from 'react';
import { Field, Form } from 'react-final-form';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { Box, Button } from '@mui/material';
import classNames from 'classnames';

import FormTextField from '@components/FormTextField/FormTextField';
import Modal from '@components/Modal/Modal';
import { IOpenClose } from '@interfaces/common';
import {
  adminCreateIngredientPending,
  adminSearchStringForAddIngredientModal,
} from '@store/features/adminInquiries/ingredientsSearch/selectors';
import removeWhiteSpaces from '@utils/removeWhiteSpaces';

import styles from './index.module.scss';

interface IProps extends IOpenClose {
  onConfirm: (values: Record<string, string>) => void;
}

const nameMaxLength = 200;
const nameMinLength = 3;

const AddIngredientOnTheFlyModal: FC<IProps> = (props: IProps): JSX.Element => {
  const { open, onClose, onConfirm } = props;

  const { t } = useTranslation();
  const isLoading = useSelector(adminCreateIngredientPending);
  const initialValue = useSelector(adminSearchStringForAddIngredientModal);

  const onFormSubmit = useCallback(
    (values: Record<string, string>) => {
      onConfirm && onConfirm(values);
    },
    [onConfirm],
  );

  const validateSupplementName = useCallback((values: Record<string, string>) => {
    if (typeof values.name !== 'string' || !values.name.trim()) {
      return {
        name: t('addIngredientModal.requiredError'),
      };
    }

    if (values.name.trim().length > nameMaxLength) {
      return {
        name: t('addIngredientModal.maxLengthError', { maxLength: nameMaxLength }),
      };
    }

    if (values.name.trim().length < nameMinLength) {
      return {
        name: t('addIngredientModal.minLengthError', { minLength: nameMinLength }),
      };
    }

    return {};
  }, []);

  return (
    <Modal
      onClose={onClose}
      open={open}
      title={t('addIngredientModal.modalTitle')}
      className="confirmation-modal"
    >
      <Box>
        <Form
          onSubmit={onFormSubmit}
          validate={validateSupplementName}
          initialValues={{ name: initialValue || '' }}
          render={props => {
            const { invalid, handleSubmit } = props;
            return (
              <form noValidate>
                <Box>
                  <Field
                    component={FormTextField}
                    label={t('addIngredientModal.ingredientName')}
                    name="name"
                    format={removeWhiteSpaces}
                    formatOnBlur
                    multiline
                    required
                    helperText={t('addIngredientModal.nameHint', {
                      maxLength: nameMaxLength,
                      minLength: nameMinLength,
                    })}
                    disabled={isLoading}
                  />
                </Box>
                <Box className={styles.buttons}>
                  <Button
                    onClick={onClose}
                    variant="text"
                    className={classNames([styles['action-button'], styles['action-button_left']])}
                  >
                    {t('common.cancel')}
                  </Button>
                  <Button
                    type="submit"
                    className={classNames([styles['action-button'], styles['action-button_right']])}
                    disabled={invalid || isLoading}
                    onClick={handleSubmit}
                    variant="contained"
                    loading={isLoading}
                  >
                    {t('addIngredientModal.confirmButton')}
                  </Button>
                </Box>
              </form>
            );
          }}
        />
      </Box>
    </Modal>
  );
};

export default AddIngredientOnTheFlyModal;
